/**
 * Format a YYYY-MM-DD date range as a human-readable string for email display.
 *
 * Pure function, no Intl/locale chicanery beyond en-US — emails go to a
 * predominantly US investor audience and consistency across recipients
 * matters more than per-locale customization.
 *
 * Output examples:
 *   ('2026-06-15', '2026-06-17')  → 'Monday, June 15 – Wednesday, June 17, 2026'
 *   ('2026-06-15', '2026-06-15')  → 'Monday, June 15, 2026'
 *   ('2026-06-30', '2026-07-02')  → 'Tuesday, June 30 – Thursday, July 2, 2026'
 *   ('2026-12-30', '2027-01-02')  → 'Wednesday, December 30, 2026 – Saturday, January 2, 2027'
 *
 * Uses an explicit noon-UTC anchor on each date to dodge timezone day-shift
 * (a midnight-UTC date can render as "the previous day" in negative offsets).
 */
export function formatEventDateRange(
  startDateStr: string,
  endDateStr: string,
): string {
  const start = parseYMD(startDateStr);
  const end = parseYMD(endDateStr);

  // Same day
  if (startDateStr === endDateStr) {
    return formatFullDate(start);
  }

  // Cross year — show both years explicitly
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${formatFullDate(start)} \u2013 ${formatFullDate(end)}`;
  }

  // Same year — show year only at the end
  return `${formatDateNoYear(start)} \u2013 ${formatDateNoYear(end)}, ${start.getUTCFullYear()}`;
}

function parseYMD(ymd: string): Date {
  // Anchor at noon UTC so toLocaleDateString in any timezone gives the
  // same calendar day. Just `new Date('2026-06-15')` would land at midnight
  // UTC, which is the previous day in any timezone west of UTC.
  return new Date(`${ymd}T12:00:00Z`);
}

function formatFullDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDateNoYear(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
